import React, { useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, CheckCircle2, ListTodo } from 'lucide-react';

interface WorkflowStep {
  title: string;
  timestamp: string;
}

interface WorkflowInfoProps {
  steps: WorkflowStep[];
  isLive?: boolean;
}

export const WorkflowInfo: React.FC<WorkflowInfoProps> = ({ steps, isLive }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const lastStepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLive && lastStepRef.current) {
      lastStepRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [steps, isLive]);

  if (!steps?.length) return null;

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="mt-3 transform transition-all duration-200 ease-in-out">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900/80 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 ease-in-out"
      >
        <div className="flex items-center space-x-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold flex items-center gap-2">
              <ListTodo size={16} className="text-blue-500" />
              Agentic flow
            </span>
            {isLive && steps.length > 0 && (
              <div 
                key={steps[steps.length - 1].timestamp} 
                className="flex items-center gap-2 animate-fadeInScale"
              >
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {steps[steps.length - 1].title}
                </span>
                <span className="flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                </span>
              </div>
            )}
          </div>
        </div>
        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      
      {isExpanded && (
        <div className="mt-2 p-5 bg-gray-50 dark:bg-gray-900/50 rounded-xl shadow-lg transform transition-all duration-200 ease-in-out">
          <div ref={contentRef} className="relative space-y-4 max-h-[300px] overflow-y-auto scrollbar-custom">
            {steps.map((step, index) => (
              <div 
                key={`${step.timestamp}-${index}`}
                ref={index === steps.length - 1 ? lastStepRef : null}
                className={`flex items-start space-x-3 text-sm relative ${
                  isLive && index === steps.length - 1
                    ? 'animate-fadeInScale'
                    : 'animate-fadeIn'
                }`}
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                <div className="flex-shrink-0 mt-1 relative z-10">
                  <div className={`transform transition-all duration-300 ${
                    isLive && index === steps.length - 1
                      ? 'scale-110'
                      : 'scale-100'
                  }`}>
                    <CheckCircle2 
                      size={18}
                      className={`${
                        isLive && index === steps.length - 1
                          ? 'text-blue-500 dark:text-blue-400'
                          : 'text-green-500 dark:text-green-400'
                      } filter drop-shadow-sm transition-colors duration-300`}
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-0 group">
                  <p className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                    {step.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 tracking-wide">
                    {formatTimestamp(step.timestamp)}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div className="absolute left-[9px] top-6 bottom-0 w-0.5 bg-gradient-to-b from-green-500/50 to-green-500/10 dark:from-green-400/50 dark:to-green-400/10 transition-all duration-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
